import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Images, Upload, Trash2, CalendarClock } from "lucide-react";
import config from "../config";
import { apiJsonHeaders } from "../utils/apiLocale";
import Spinner from "./common/Spinner";
import Button from "./common/Button";
import { Textarea } from "./common/Textarea";
import { Label } from "./common/Label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./common/Card";
import customToast from "./common/Toaster";
import { formatDateTimeDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";

async function uploadImageToImgbb(file) {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("key", config.imgbbApiKey);
  const response = await fetch("https://api.imgbb.com/1/upload", {
    method: "POST",
    body: formData,
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(
      result?.error?.message || "No se pudo subir la imagen a imgBB"
    );
  }
  return result.data.url;
}

function formatUploadedAt(iso) {
  if (!iso) return "—";
  return formatDateTimeDMY(iso) || "—";
}

export default function PatientStudiesTab({ userId }) {
  const { t } = useBrandTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const {
    data: studies = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["patient-studies", userId],
    queryFn: async () => {
      const res = await fetch(`/api/patient-studies/${userId}`, {
        headers: apiJsonHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Error al cargar los estudios");
      }
      return res.json();
    },
    enabled: !!userId,
  });

  const insertMutation = useMutation({
    mutationFn: async ({ imageUrl, comment }) => {
      const res = await fetch("/api/patient-studies", {
        method: "POST",
        headers: apiJsonHeaders(),
        body: JSON.stringify({
          patient_id: userId,
          image_url: imageUrl,
          comment: comment?.trim() || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Error al guardar el estudio");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-studies", userId] });
      customToast(t("app.clinical.studySaved", "Estudio guardado correctamente"), "success");
      setPendingFile(null);
      setNewComment("");
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (err) => {
      console.error(err);
      customToast(err.message || t("app.studies.saveError", "Error al guardar el estudio"), "danger");
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ id, comment }) => {
      const res = await fetch(`/api/patient-studies/${id}`, {
        method: "PATCH",
        headers: apiJsonHeaders(),
        body: JSON.stringify({
          patient_id: userId,
          comment: comment?.trim() || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Error al actualizar el comentario");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-studies", userId] });
      customToast(t("app.clinical.commentUpdated", "Comentario actualizado"), "success");
    },
    onError: (err) => {
      console.error(err);
      customToast(err.message || t("app.studies.updateError", "Error al actualizar"), "danger");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(
        `/api/patient-studies/${id}?patient_id=${encodeURIComponent(userId)}`,
        {
          method: "DELETE",
          headers: apiJsonHeaders(),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Error al eliminar el estudio");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-studies", userId] });
      customToast(t("app.clinical.studyDeleted", "Estudio eliminado"), "success");
    },
    onError: (err) => {
      console.error(err);
      customToast(err.message || t("app.studies.deleteError", "Error al eliminar"), "danger");
    },
  });

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      customToast(t("app.clinical.selectImage", "Seleccioná un archivo de imagen"), "danger");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      customToast(t("app.clinical.imageTooLarge8", "La imagen no puede superar los 8 MB"), "danger");
      return;
    }
    setPendingFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmitNew = async () => {
    if (!pendingFile) {
      customToast(t("app.clinical.chooseImage", "Elegí una imagen para subir"), "danger");
      return;
    }
    try {
      const url = await uploadImageToImgbb(pendingFile);
      await insertMutation.mutateAsync({
        imageUrl: url,
        comment: newComment,
      });
    } catch (err) {
      console.error(err);
      customToast(err.message || t("app.studies.uploadError", "Error al subir la imagen"), "danger");
    }
  };

  const uploading = insertMutation.isPending;

  return (
    <div className="space-y-6 pb-16">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{t("app.profile.tabStudies", "Mis Estudios")}</h2>
      <p className="text-sm text-gray-600 mb-4">
        {t(
          "app.studies.pageIntro",
          "Subí fotos o capturas de tus estudios médicos y agregá una nota para recordar el contexto. Las imágenes se almacenan de forma segura mediante imgBB."
        )}
      </p>

      <Card className="overflow-hidden border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-5 w-5 text-slate-500" />
            {t("app.studies.uploadTitle", "Subir nuevo estudio")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {t("app.studies.imageLabel", "Imagen")}
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePickFile}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {t("app.studies.chooseFile", "Elegir archivo")}
              </Button>
              {pendingFile && (
                <span className="text-sm text-slate-600 truncate max-w-[200px]">
                  {pendingFile.name}
                </span>
              )}
            </div>
          </div>
          {previewUrl && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 overflow-hidden">
              <img
                src={previewUrl}
                alt={t("app.studies.previewAlt", "Vista previa")}
                className="max-h-52 w-full object-contain mx-auto rounded"
              />
            </div>
          )}
          <div>
            <Label htmlFor="study-new-comment" className="block text-sm font-medium text-gray-700 mb-2">
              {t("app.studies.commentOptional", "Comentario (opcional)")}
            </Label>
            <Textarea
              id="study-new-comment"
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t("app.studies.commentPlaceholder", "Ej.: Resonancia lumbar abril 2026, solicitada por traumatología…")}
              className="w-full rounded border border-slate-200 p-3 text-sm text-slate-700 resize-y min-h-[80px]"
              disabled={uploading}
            />
          </div>
          <Button
            type="button"
            onClick={handleSubmitNew}
            disabled={uploading || !pendingFile}
          >
            {uploading
              ? t("app.studies.uploading", "Subiendo…")
              : t("app.studies.saveStudy", "Guardar estudio")}
          </Button>
        </CardContent>
      </Card>

      {isLoading && <Spinner className="w-10 h-10 mx-auto" />}
      {error && (
        <p className="text-red-600 text-sm">
          {t(
            "app.studies.loadError",
            "No se pudieron cargar tus estudios. Si el problema continúa, contactá soporte."
          )}
        </p>
      )}

      {!isLoading && studies.length === 0 && (
        <Card className="border border-dashed border-slate-200 bg-slate-50/80">
          <CardContent className="py-10 text-center text-sm text-gray-500">
            <Images className="h-10 w-10 mx-auto mb-2 text-slate-400" />
            {t(
              "app.studies.emptyState",
              "Todavía no cargaste estudios. Usá el formulario de arriba para agregar el primero."
            )}
          </CardContent>
        </Card>
      )}

      {studies.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <Images className="h-4 w-4 text-slate-500" />
            {t("app.studies.yourStudies", "Tus estudios ({{count}})", {
              count: studies.length,
            })}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {studies.map((study) => (
              <StudyCard
                key={study.id}
                study={study}
                onSaveComment={(comment) =>
                  updateCommentMutation.mutate({ id: study.id, comment })
                }
                onDelete={() => {
                  if (
                    window.confirm(t("app.studies.confirmDelete", "¿Eliminar este estudio? La imagen dejará de mostrarse en tu perfil."))
                  ) {
                    deleteMutation.mutate(study.id);
                  }
                }}
                savingComment={updateCommentMutation.isPending}
                deleting={deleteMutation.isPending}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StudyCard({
  study,
  onSaveComment,
  onDelete,
  savingComment,
  deleting,
}) {
  const { t } = useBrandTranslation();
  const [comment, setComment] = useState(study.comment || "");

  useEffect(() => {
    setComment(study.comment || "");
  }, [study.id, study.comment]);

  return (
    <Card className="overflow-hidden border border-slate-200 shadow-sm flex flex-col">
      <div className="aspect-video bg-slate-100 border-b border-slate-200 flex items-center justify-center overflow-hidden">
        <a
          href={study.image_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-full"
        >
          <img
            src={study.image_url}
            alt={t("app.studies.studyAlt", "Estudio")}
            className="w-full h-full object-contain max-h-56"
          />
        </a>
      </div>
      <CardContent className="pt-4 space-y-3 flex-1 flex flex-col">
        <div className="flex items-start gap-2 text-xs text-slate-600">
          <CalendarClock className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
          <div>
            <span className="font-medium text-slate-700">{t("app.studies.uploaded", "Subida:")}</span>{" "}
            {formatUploadedAt(study.uploaded_at)}
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <Label className="text-xs text-gray-700">{t("app.studies.comment", "Comentario")}</Label>
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full rounded border border-slate-200 p-2 text-sm text-slate-700 resize-y min-h-[72px]"
            disabled={savingComment || deleting}
          />
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onSaveComment(comment)}
            disabled={
              savingComment ||
              deleting ||
              (comment || "") === (study.comment || "")
            }
          >
            {savingComment
              ? t("app.studies.savingComment", "Guardando…")
              : t("app.studies.saveComment", "Guardar comentario")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-red-700 border-red-200 hover:bg-red-50"
            onClick={onDelete}
            disabled={deleting || savingComment}
          >
            <Trash2 className="h-4 w-4 mr-1 inline" />
            {t("app.studies.delete", "Eliminar")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
